import { type PaginationState } from '@tanstack/react-table'
import { type Dispatch, type FC, type SetStateAction } from 'react'

import {
  type MultipleSelectedValuesFilter,
  type SingleSelectedValueFilter,
} from 'components/brokers/types'
import EnterpriseFiltersView, { type FilterConfigurations } from 'components/reusable/filters/EnterpriseFiltersView'
import { type ComboboxFilterGroup } from 'components/reusable/table/ComboboxMultiSelectFilter'

import EmployerOECohortFilter from './EmployerOECohortFilter'
import EmployerOEEmployerFilter from './EmployerOEEmployerFilter'
import EmployerOEMaxEmployeeCountFilter from './EmployerOEMaxEmployeeCountFilter'
import EmployerOEMaxFinalRatesFilter from './EmployerOEMaxFinalRatesFilter'
import EmployerOEMinEmployeeCountFilter from './EmployerOEMinEmployeeCountFilter'
import EmployerOEMinFinalRatesFilter from './EmployerOEMinFinalRatesFilter'
import EmployerOEStatusFilter from './EmployerOEStatusFilter'
import { type EmployerOpenEnrollmentFilters } from './EmployerOpenEnrollmentTable'

export type EmployerOEFilterGroupType = 'Configuration' | 'Employer' | 'Metrics'

type MultiSelectFilterComponents = Record<
  string,
  FC<MultipleSelectedValuesFilter>
>

type SingleSelectFilterComponents = Record<
  string,
  FC<SingleSelectedValueFilter>
>

export const employerOEMultiSelectFilters: MultiSelectFilterComponents = {
  status: EmployerOEStatusFilter,
  cohort: EmployerOECohortFilter,
  employer_ids: EmployerOEEmployerFilter,
}

export const employerOESingleSelectFilters: SingleSelectFilterComponents = {
  min_employee_count: EmployerOEMinEmployeeCountFilter,
  max_employee_count: EmployerOEMaxEmployeeCountFilter,
  min_final_rates: EmployerOEMinFinalRatesFilter,
  max_final_rates: EmployerOEMaxFinalRatesFilter,
}

export const filterConfigurations: FilterConfigurations<
  EmployerOpenEnrollmentFilters,
  EmployerOEFilterGroupType
> = {
  status: {
    key: 'status',
    label: 'Status',
    group: 'Configuration',
    isMultiValue: true,
  },
  cohort: {
    key: 'cohort',
    label: 'Cohort',
    group: 'Configuration',
    isMultiValue: true,
  },
  employer_ids: {
    key: 'employer_ids',
    label: 'Employer',
    group: 'Employer',
    isMultiValue: true,
  },
  min_employee_count: {
    key: 'min_employee_count',
    label: 'Min Employee Count',
    group: 'Metrics',
    isMultiValue: false,
  },
  max_employee_count: {
    key: 'max_employee_count',
    label: 'Max Employee Count',
    group: 'Metrics',
    isMultiValue: false,
  },
  min_final_rates: {
    key: 'min_final_rates',
    label: 'Min Final Rates %',
    group: 'Metrics',
    isMultiValue: false,
  },
  max_final_rates: {
    key: 'max_final_rates',
    label: 'Max Final Rates %',
    group: 'Metrics',
    isMultiValue: false,
  },
}

export const getEmployerOEFiltersConfigurationSortValue = (
  filterConfiguration: ComboboxFilterGroup<string>
): number => {
  switch (filterConfiguration.groupLabel) {
    case 'Configuration':
      return 0
    case 'Metrics':
      return 1
    case 'Employer':
      return 2
    default:
      return 3
  }
}

type EmployerOpenEnrollmentFiltersViewProps = {
  filters: EmployerOpenEnrollmentFilters
  setFilters: Dispatch<SetStateAction<EmployerOpenEnrollmentFilters>>
  pagination: PaginationState
  setPagination: Dispatch<SetStateAction<PaginationState>>
}

const EmployerOpenEnrollmentFiltersView = ({
  filters,
  setFilters,
  pagination,
  setPagination,
}: EmployerOpenEnrollmentFiltersViewProps) => {
  return (
    <EnterpriseFiltersView
      filters={filters}
      setFilters={setFilters}
      pagination={pagination}
      setPagination={setPagination}
      filterConfigurations={filterConfigurations}
      singleSelectFilters={employerOESingleSelectFilters}
      multiSelectFilters={employerOEMultiSelectFilters}
      defaultPinnedOptionsByFilterValue={{}}
      getFiltersConfigurationSortValue={getEmployerOEFiltersConfigurationSortValue}
    />
  )
}

export default EmployerOpenEnrollmentFiltersView
